// @ts-nocheck
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, IdCard, GraduationCap } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { PendingTab, IssuedTab } from "@/design-system/event-editor/MediaCredentialsSection";
import { CoachesTab } from "@/design-system/event-editor/CoachAttendanceSection";
import { parseLocalDate } from '@/utils/formatters';

type Section = "media" | "coaches";
type MediaTab = "pending" | "issued";

export default function EventCredentialsCoachesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();

  const { data: event } = useQuery({
    queryKey: ["credentials-coaches-event", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("events")
        .select(
          "id, name, start_date, end_date, media_credentials_enabled, media_credentials_paid, coach_attendance_enabled, coach_attendance_paid, is_ncaa_live_event"
        )
        .eq("id", eventId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const mediaOn = !!event?.media_credentials_enabled;
  const coachesOn = !!event?.coach_attendance_enabled;

  const [section, setSection] = useState<Section>("media");
  const [mediaTab, setMediaTab] = useState<MediaTab>("pending");

  // Default to whichever is enabled
  const effectiveSection: Section =
    section === "media" && !mediaOn && coachesOn ? "coaches" :
    section === "coaches" && !coachesOn && mediaOn ? "media" :
    section;

  const eventDates =
    event?.start_date && event?.end_date
      ? `${format(parseLocalDate(event.start_date), "MMM d")} – ${format(parseLocalDate(event.end_date), "MMM d, yyyy")}`
      : "";

  return (
    <div className="space-y-6 px-4 pt-6 md:px-6 md:pt-10 lg:px-8">
      <div>
        <button
          onClick={() => navigate(`/admin-dashboard/events/${eventId}/edit`)}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to event editor
        </button>
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
          <IdCard className="w-6 h-6" />
          Credentials & Coaches
        </h1>
        <p className="text-muted-foreground">
          {event?.name} {eventDates && <span>· {eventDates}</span>}
        </p>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => setSection("media")}
          className={cn(
            "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border",
            effectiveSection === "media" ? "border-primary bg-primary/10 text-foreground" : "border-input text-muted-foreground hover:text-foreground"
          )}
        >
          <IdCard className="w-4 h-4" /> Media Credentials
        </button>
        <button
          onClick={() => setSection("coaches")}
          className={cn(
            "inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border",
            effectiveSection === "coaches" ? "border-primary bg-primary/10 text-foreground" : "border-input text-muted-foreground hover:text-foreground"
          )}
        >
          <GraduationCap className="w-4 h-4" /> College Coaches
        </button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {!eventId ? null : effectiveSection === "media" ? (
            !mediaOn ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Media credentials are not enabled for this event. Enable them in the event editor's Coaches / Media step.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex border-b border-border">
                  {([
                    { id: "pending", label: "Requests" },
                    { id: "issued", label: "Issued Credentials" },
                  ] as { id: MediaTab; label: string }[]).map((t) => (
                    <button key={t.id} onClick={() => setMediaTab(t.id)}
                      className={cn(
                        "px-4 py-2 text-sm font-medium border-b-2 -mb-px",
                        mediaTab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                      )}>
                      {t.label}
                    </button>
                  ))}
                </div>
                {mediaTab === "pending" && <PendingTab eventId={eventId} paid={!!event?.media_credentials_paid} />}
                {mediaTab === "issued" && <IssuedTab eventId={eventId} />}
              </div>
            )
          ) : (
            !coachesOn ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Coach attendance is not enabled for this event. Enable it in the event editor's Coaches / Media step.
              </div>
            ) : (
              <CoachesTab eventId={eventId} isNcaaLive={!!event?.is_ncaa_live_event} />
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
